<template>
	<section v-if="items.length" class="content-section faq-section">
		<div class="row row-header align-center">
			<h2>{{ header }}</h2>
		</div>
		<div class="row faq-row">
			<div class="columns column12">
				<div v-for="(item, index) in items" :key="index" class="faq-item">
					<collapse
						:header="item.question"
						:content="item.answer"
						:is-open="activeIndex === index"
						@toggle="toggleCollapse(index)"
					/>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
const config = useRuntimeConfig();

const { locale } = useI18n();

const props = withDefaults(
	defineProps<{
		header: string;
		items: FrequentlyAskedQuestion['faqitems'];
		schema?: boolean;
	}>(),
	{
		schema: true,
	},
);

const activeIndex = ref<number | null>(null);

const toggleCollapse = (index: number) => {
	activeIndex.value = activeIndex.value === index ? null : index;
};

if (props.schema && props.items.length) {
	const { defaults, fetchDefaults } = useDefaults();

	await fetchDefaults();

	const restaurantName = defaults.value[locale.value].website.restaurantName || 'Café de Jeugd';

	const faqSchema = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		name: `Official ${restaurantName} FAQ`,
		about: {
			'@type': 'Restaurant',
			'@id': `${config.public.siteUrl}#restaurant`,
		},
		mainEntity: props.items.map((item) => ({
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.answer,
			},
		})),
	};

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(faqSchema),
			},
		],
	});
}
</script>

<style lang="scss" scoped>
.faq-section {
	margin: 0 auto 75px;
}

.faq-item {
	text-align: left;
	margin-top: -1px;

	:deep(.container) {
		text-transform: uppercase;
	}

	:deep(.content) {
		font-family: var(--heading-font-family);
	}

	:deep(.container:not(.not-expanded) .header),
	:deep(.container:not(.not-expanded) .content) {
		background-color: var(--primary-background-color);
	}

	&:first-child {
		margin-top: 0;
	}
}

.row-header {
	h2 {
		width: 100%;
	}
}

.faq-row {
	margin: 15px auto 25px;
	max-width: 920px;
	overflow: hidden;
	padding: 0;
	width: 92%;

	.column12 {
		padding: 0;
	}
}
</style>
